// frontend/src/context/UserContext.tsx
import React, { createContext, useState, useEffect } from 'react';
import { User } from '../models/User';
import { UserService } from '../services/userService';
import { AuthService } from '../services/authService';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../components/Dialog';
import { Button } from '../components/Button';
import Loading from '../components/Loading';
import { useLocation } from 'react-router-dom';
import showErrorToast from '../components/Toasts/ShowErrorToast';
import showSuccessToast from '../components/Toasts/ShowSuccessToast';

interface UserContextType {
    currentUser: User | null;
    setCurrentUser: (user: User | null) => void;
    logout: () => void;
    triggerSessionExpiry: () => void;
    sessionTimeRemaining: number;
    isSessionExpiringSoon: boolean;
}

export const UserContext = createContext<UserContextType>({
    currentUser: null,
    setCurrentUser: () => { },
    logout: () => { },
    triggerSessionExpiry: () => { },
    sessionTimeRemaining: 0,
    isSessionExpiringSoon: false,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number>(0);
    const [isSessionExpiringSoon, setIsSessionExpiringSoon] = useState<boolean>(false);
    const { pathname } = useLocation();

    // Update session time remaining
    const updateSessionTime = () => {
        const timeRemaining = AuthService.getTimeUntilExpiry();
        setSessionTimeRemaining(timeRemaining);
        setIsSessionExpiringSoon(AuthService.isTokenExpiringSoon());
    };

    useEffect(() => {
        // Skip entirely on the login page itself - the user is explicitly there to
        // (re)authenticate, so a leftover token (e.g. stale after a server restart that
        // invalidated it server-side, even though it hasn't hit its client-side expiry yet)
        // must never pop the "Session Expired" dialog over the login form and bury the
        // real invalid-credentials feedback from a failed login attempt.
        if (pathname === '/') {
            return;
        }

        const token = AuthService.getToken();
        if (token && !AuthService.isTokenExpired()) {
            // Initialize token monitoring
            AuthService.initializeTokenMonitoring();

            UserService.getProfile()
                .then((user) => {
                    if (user) {
                        setCurrentUser(user);
                        updateSessionTime();
                    } else {
                        triggerSessionExpiry();
                    }
                })
                .catch(() => triggerSessionExpiry());
        } else {
            const isPublicTrackingPage =
                pathname.includes('/gp/tracking/delivery/confirmation') ||
                pathname.includes('/gp/tracking/delivery/success') ||
                pathname.includes('/gp/tracking/standalone/delivery-man') ||
                pathname.includes('/gp/tracking/standalone/sub-delivery-man') ||
                pathname.includes('/gp/tracking/standalone/customer') ||
                pathname.includes('/gp/tracking/standalone/success');
            if (pathname.includes('/gp') && !isPublicTrackingPage) {
                triggerSessionExpiry();
            }
        }
    }, [pathname]);

    // Update session time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            if (currentUser) {
                updateSessionTime();
            }
        }, 60000); // Every minute

        return () => clearInterval(interval);
    }, [currentUser]);

    // Listen for token expiration and extension events
    useEffect(() => {
        const handleTokenExpired = () => {
            setCurrentUser(null);
            setSessionTimeRemaining(0);
            setIsSessionExpiringSoon(false);
            triggerSessionExpiry();
        };

        const handleSessionExtended = () => {
            // Update session time after extension
            updateSessionTime();
            setIsSessionExpiringSoon(false);
        };

        window.addEventListener('tokenExpired', handleTokenExpired);
        window.addEventListener('sessionExtended', handleSessionExtended);

        return () => {
            window.removeEventListener('tokenExpired', handleTokenExpired);
            window.removeEventListener('sessionExtended', handleSessionExtended);
        };
    }, []);

    const logout = () => {
        AuthService.logout();
        setCurrentUser(null);
        setSessionTimeRemaining(0);
        setIsSessionExpiringSoon(false);
        window.location.assign('/');
    };

    const triggerSessionExpiry = () => {
        setIsDialogOpen(true);
    };

    const handleLogout = () => {
        setIsLoggingOut(true);
        try {
            logout();
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            setIsLoggingOut(false);
            setIsDialogOpen(false);
        }
    };

    const handleExtendSession = async () => {
        try {
            // Check if token can be extended
            if (!AuthService.canExtendToken()) {
                showErrorToast('Session cannot be extended. Please log in again.');
                handleLogout();
                return;
            }

            // Try to refresh the token
            const success = await AuthService.attemptTokenRefresh();
            if (success) {
                showSuccessToast('Session extended successfully!');
                setIsDialogOpen(false);
                updateSessionTime();
            } else {
                showErrorToast('Unable to extend session. Please log in again.');
                handleLogout();
            }
        } catch (error) {
            showErrorToast('Failed to extend session. Please log in again.');
            handleLogout();
        }
    };

    return (
        <UserContext.Provider value={{
            currentUser,
            setCurrentUser,
            logout,
            triggerSessionExpiry,
            sessionTimeRemaining,
            isSessionExpiringSoon
        }}>
            {children}

            {/* Session Expired Dialog */}
            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                <DialogTitle>Session Expired</DialogTitle>
                <DialogDescription>
                    {AuthService.canExtendToken()
                        ? "Your session has expired. You can try to extend your session or log in again."
                        : "Your session has expired. Please log in again."
                    }
                </DialogDescription>
                <DialogActions>
                    {AuthService.canExtendToken() && (
                        <Button onClick={handleExtendSession} disabled={isLoggingOut}>
                            {isLoggingOut ? <Loading /> : 'Extend Session'}
                        </Button>
                    )}
                    <Button onClick={handleLogout} disabled={isLoggingOut}>
                        {isLoggingOut ? <Loading /> : 'Back to Login'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Session Warning Toast */}
            {isSessionExpiringSoon && sessionTimeRemaining > 0 && (
                <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded shadow-lg z-50">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium">
                                Session expires in {sessionTimeRemaining} minute{sessionTimeRemaining > 1 ? 's' : ''}
                            </p>
                            <p className="text-xs mt-1">
                                Please save your work and log in again if needed.
                            </p>
                        </div>
                        <div className="ml-auto pl-3">
                            <button
                                onClick={() => setIsSessionExpiringSoon(false)}
                                className="text-yellow-400 hover:text-yellow-600"
                            >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UserContext.Provider>
    );
};
